import { useState } from "react";
import { Merge, Loader2, UserCircle2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";

// Por defecto: queda el que tiene voz (o más fotos); foto = la que tenga más ángulos; voz = la que exista.
const score = (c) => (c.voice_path ? 4 : 0) + (c.photo_path ? 1 : 0) + (c.photo_profile_path ? 1 : 0) + (c.photo_body_path ? 1 : 0);
const photoScore = (c) => (c.photo_path ? 1 : 0) + (c.photo_profile_path ? 1 : 0) + (c.photo_body_path ? 1 : 0);

const Option = ({ on, onClick, disabled, children, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`flex-1 flex items-center gap-2 p-2.5 rounded-xl border text-left text-sm transition-colors disabled:opacity-40 ${on ? "border-[#FF334B] bg-[#FF334B]/10" : "border-white/10 hover:border-white/25"}`}>
    {children}{on && <Check className="w-4 h-4 text-[#FF334B] ml-auto shrink-0" />}
  </button>
);

export const MergeCharactersDialog = ({ characters, pair, onClose, onMerged }) => {
  const [a0, b0] = [...pair].sort((x, y) => score(y) - score(x));
  const [aId, setAId] = useState(a0.id);
  const [bId, setBId] = useState(b0.id);
  const a = characters.find((c) => c.id === aId) || a0;
  const b = characters.find((c) => c.id === bId) || b0;
  const [photoFrom, setPhotoFrom] = useState(photoScore(b0) > photoScore(a0) ? "b" : "a");
  const [voiceFrom, setVoiceFrom] = useState(a0.voice_path ? "a" : "b");
  const [name, setName] = useState((a0.name || "").trim());
  const [busy, setBusy] = useState(false);
  const pick = (k) => (k === "a" ? a : b);

  const merge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/characters/merge", {
        keep_id: a.id, remove_id: b.id, name,
        photo_from: photoFrom === "a" ? "keep" : "remove", voice_from: voiceFrom === "a" ? "keep" : "remove",
      });
      toast.success(`Personajes unidos. Se actualizaron ${r.data.series_updated} serie(s) y ${r.data.projects_updated} proyecto(s).`);
      onMerged?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron unir"); } finally { setBusy(false); }
  };

  const sel = (val, set, testId) => (
    <select value={val} onChange={(e) => set(e.target.value)} data-testid={testId}
      className="w-full bg-[#0B0D13] border border-white/12 rounded-lg px-3 py-2 text-sm">
      {characters.map((c) => <option key={c.id} value={c.id}>{c.name}{c.voice_path ? " · voz ✓" : " · sin voz"}</option>)}
    </select>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="merge-modal" onClick={onClose}>
      <div className="mfp-card max-w-lg w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2"><Merge className="w-5 h-5 text-[#FF9F2E]" /><h3 className="font-head text-xl font-bold">Unir personajes</h3></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[11px] text-[#64748B]">Se queda</label>{sel(aId, setAId, "merge-keep-select")}</div>
          <div><label className="text-[11px] text-[#64748B]">Se borra (se une al otro)</label>{sel(bId, setBId, "merge-remove-select")}</div>
        </div>
        <div>
          <label className="text-[11px] text-[#64748B]">Nombre final</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="merge-name-input"
            className="w-full bg-[#0B0D13] border border-white/12 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="space-y-2">
          <div className="text-[11px] text-[#64748B]">Foto que se conserva</div>
          <div className="flex gap-2">
            {["a", "b"].map((k) => (
              <Option key={k} on={photoFrom === k} onClick={() => setPhotoFrom(k)} disabled={!pick(k).photo_path} testId={`merge-photo-${k}`}>
                {pick(k).photo_path ? <img src={mediaUrl(pick(k).photo_path)} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <UserCircle2 className="w-10 h-10" />}
                <span className="truncate">{pick(k).name}<span className="block text-[10px] text-[#64748B]">{photoScore(pick(k))} foto(s)</span></span>
              </Option>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-[11px] text-[#64748B]">Voz que se conserva</div>
          <div className="flex gap-2">
            {["a", "b"].map((k) => (
              <Option key={k} on={voiceFrom === k} onClick={() => setVoiceFrom(k)} disabled={!pick(k).voice_path} testId={`merge-voice-${k}`}>
                <span className="truncate">{pick(k).name}<span className="block text-[10px] text-[#64748B]">{pick(k).voice_path ? "voz ✓" : "sin voz"}</span></span>
              </Option>
            ))}
          </div>
          {pick(voiceFrom).voice_path && <audio src={mediaUrl(pick(voiceFrom).voice_path)} controls className="w-full h-9" data-testid="merge-voice-preview" />}
        </div>
        <p className="text-xs text-[#94A3B8]">Todas las series, capítulos y escenas que usaban a «{b.name.trim()}» pasarán a «{name || a.name.trim()}». Después se borra el duplicado. Las escenas ya generadas no cambian.</p>
        <div className="flex gap-3">
          <button data-testid="merge-cancel" onClick={onClose} className="flex-1 py-3 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">Cancelar</button>
          <button data-testid="merge-confirm" onClick={merge} disabled={busy || a.id === b.id || !name.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF9F2E] text-black font-semibold py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 spin" /> : <Merge className="w-5 h-5" />} Unir
          </button>
        </div>
      </div>
    </div>
  );
};
